import { useEffect, useMemo, useState } from 'react';
import type { Rect } from '@view-model/models/common/basic/Rect';
import type { ElementId, ModelId, ViewModelId } from 'balus-common';
import { ElementDescriptionContent } from './components';
import type { ElementDescription } from './domain';
import { ElementDescriptionRepository } from './domain';
import type { ElementDescriptionTarget } from './domain/ElementDescriptionTarget';
import type { ICalloutTarget } from '@model-framework/ui';

type Props = {
  viewModelId: ViewModelId;
  modelId: ModelId;
  target: ElementDescriptionTarget;
  elementId: ElementId;
  elementRect: Rect;
  readonly: boolean;
  /** しっぽ先端の交点計算に使うターゲット。省略時は elementRect（矩形）として計算。楕円ゾーンには EllipseTarget を渡す。 */
  targetCalloutTarget?: ICalloutTarget;
};

/**
 * 要素への補足説明（吹き出し）
 *
 * @param elementId
 * @param elementRect 言及対象要素の矩形領域
 * @param readonly 編集可能かどうか
 * @param nodeRepository
 */
export const ElementDescriptionView: React.FC<Props> = ({
  viewModelId,
  modelId,
  target,
  elementId,
  elementRect,
  readonly,
  targetCalloutTarget,
}: Props) => {
  const repository = useMemo(() => {
    return ElementDescriptionRepository.buildFromIds(viewModelId, modelId, target, elementId);
  }, [modelId, elementId, target, viewModelId]);

  const [description, setDescription] = useState<ElementDescription | null>(null);

  // 「Can't perform a React state update on an unmounted component〜」というwarningは無視しても問題ない
  // cf. https://github.com/levii/balus-app/discussions/2686
  useEffect(() => {
    repository.addListener((desc) => {
      setDescription(desc);
    });

    return () => repository.removeListener();
  }, [repository]);

  if (!description?.isVisible()) return null;

  return (
    <ElementDescriptionContent
      viewModelId={viewModelId}
      elementId={elementId}
      description={description}
      readonly={readonly}
      elementRect={elementRect}
      repository={repository}
      targetCalloutTarget={targetCalloutTarget}
    />
  );
};
